import { create } from 'zustand';
import { adminAPI } from '../services/api';

const USER_KEY = 'user';
const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refreshToken';

const readJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.error(`Failed to parse ${key}:`, error);
    return null;
  }
};

const persistAuth = ({ token, refreshToken, user }) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }

  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  } else {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  if (user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(USER_KEY);
  }
};

export const useAuthStore = create((set, get) => ({
  user: readJson(USER_KEY),
  token: localStorage.getItem(TOKEN_KEY) || null,
  refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY) || null,
  isAuthenticated: Boolean(localStorage.getItem(TOKEN_KEY)),
  isLoading: false,
  error: null,
  setAuth: (token, refreshToken, user) => {
    persistAuth({ token, refreshToken, user });
    set({
      token,
      refreshToken: refreshToken || null,
      user,
      isAuthenticated: Boolean(token),
      error: null
    });
  },
  setUser: (user) => {
    persistAuth({
      token: get().token,
      refreshToken: get().refreshToken,
      user
    });
    set({ user });
  },
  setToken: (token) => {
    persistAuth({
      token,
      refreshToken: get().refreshToken,
      user: get().user
    });
    set({ token, isAuthenticated: Boolean(token) });
  },
  setRefreshToken: (refreshToken) => {
    persistAuth({
      token: get().token,
      refreshToken,
      user: get().user
    });
    set({ refreshToken });
  },
  setActiveRole: (activeRole) => {
    const currentUser = get().user;
    if (!currentUser) return;

    const nextUser = {
      ...currentUser,
      activeRole,
      role: activeRole
    };

    persistAuth({
      token: get().token,
      refreshToken: get().refreshToken,
      user: nextUser
    });
    set({ user: nextUser });
  },
  checkAuth: async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    const user = readJson(USER_KEY);

    set({
      token: token || null,
      refreshToken: refreshToken || null,
      user,
      isAuthenticated: Boolean(token)
    });

    if (!token) return false;
    set({ isLoading: true });
    try {
      const response = await adminAPI.getAdminMe();
      const freshUser = response.data?.data;
      if (!freshUser) throw new Error('Missing admin identity');
      persistAuth({ token, refreshToken, user: freshUser });
      set({ user: freshUser, isAuthenticated: true, isLoading: false });
      return true;
    } catch (error) {
      persistAuth({ token: null, refreshToken: null, user: null });
      set({ token: null, refreshToken: null, user: null, isAuthenticated: false, isLoading: false });
      return false;
    }
  },
  // Saving roles/accounts refreshes the menu identity, but an isolated
  // refresh failure is not proof that the operator's session is invalid.
  refreshIdentity: async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!token) return false;

    try {
      const response = await adminAPI.getAdminMe();
      const freshUser = response.data?.data;
      if (!freshUser) return false;
      persistAuth({ token, refreshToken, user: freshUser });
      set({ token, refreshToken: refreshToken || null, user: freshUser, isAuthenticated: true, error: null });
      return true;
    } catch (error) {
      return false;
    }
  },
  // App startup uses this name. Keep its behavior aligned with refreshIdentity:
  // a failed non-auth feature refresh must not erase an otherwise valid session.
  refreshAdminSession: () => get().refreshIdentity(),
  logout: () => {
    persistAuth({ token: null, refreshToken: null, user: null });
    set({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      error: null
    });
  },
  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error })
}));
